import { appLocalStorage } from '../../../utils/storage';
import { getLocalStorage, setLocalStorage } from '../../../utils/func';
import { TextSelection } from 'prosemirror-state';
import { opinionDraftId } from '../../../media/draftIdentity';
import { readGuestOpinion, readGuestHandoff, saveGuestDraft, completeGuestHandoff } from '../../../guestTrial/store';
import { assertDraftOwnerActive, isDeletedOwner } from '../../../accountDeletion/ownerState';

const read = key => {
  try { return getLocalStorage(key); } catch { return null; }
};
const write = (key, value) => {
  try { setLocalStorage(key, value); return true; } catch { return false; }
};
const remove = key => {
  try { appLocalStorage.removeItem(key); } catch { /* Keep the in-memory draft usable. */ }
};

export const pendingOpinionKey = (userId, storyId) => `comment-pending:${userId}:${storyId}`;
export const fullOpinionKey = (userId, storyId) => `comment:${userId}:${storyId}`;

// A full draft replaces the saved opinion. Text entered before we know that
// opinion is an addition, and must not be mistaken for a replacement on reload.
export function createOpinionDraft(userId, storyId, returnPath) {
  assertDraftOwnerActive(userId);
  // Legacy comment${storyId} has no owner. Leave it untouched instead of
  // silently attributing another account's private text to this session.
  const key = fullOpinionKey(userId, storyId);
  const pendingKey = pendingOpinionKey(userId, storyId);
  let full = userId ? read(key) : null;
  const persistedHtml = full;
  let addition = userId && full === null ? read(pendingKey) : null;
  const guest = !userId ? readGuestOpinion(storyId) : null;
  const handoff = userId ? readGuestHandoff(storyId, userId, returnPath) : null;
  const markerKey = `comment-guest-import:${userId}:${storyId}`;
  let marker;
  try { if (userId) marker = JSON.parse(read(markerKey)); } catch { /* Rebuild a damaged marker. */ }
  let importedScore;
  let pendingHandoff;
  if (userId && returnPath && marker?.id === new URL(returnPath, 'https://wiw.invalid').searchParams.get('guestDraft')
    && Number.isInteger(marker.score) && marker.score >= 1 && marker.score <= 7
    && Date.now() >= marker.createdAt && Date.now() - marker.createdAt < 7 * 24 * 60 * 60 * 1000) importedScore = marker.score;
  if (handoff) {
    // Keep an existing account draft, including a deliberate empty full draft.
    // The journal distinguishes an accepted write from an account draft that
    // changed independently while the first transfer was unable to persist.
    const target = full !== null ? key : pendingKey;
    const before = full ?? addition ?? '';
    const sameIntent = marker?.id === handoff.id;
    const existing = sameIntent && marker.target === target;
    importedScore = handoff.score;
    if (sameIntent && marker.applied) {
      completeGuestHandoff(handoff.id);
    } else if (existing && before === marker.merged) {
      // Retain the durable-write proof before pending becomes a full draft.
      if (write(markerKey, JSON.stringify({ ...marker, applied: true }))) completeGuestHandoff(handoff.id);
      else pendingHandoff = { id: handoff.id, markerKey, score: handoff.score };
    } else {
      const merged = before + handoff.html;
      // Even when durable storage is temporarily full, keep the complete
      // recovered text visible and editable. Do not discard the source buffer.
      if (full !== null) full = merged;
      else addition = merged;
      pendingHandoff = { id: handoff.id, markerKey, score: handoff.score };
    }
  }
  const result = {
    key, pendingKey, id: userId ? opinionDraftId(userId, storyId) : `guest:${storyId}`, html: full ?? addition ?? guest?.html ?? '',
    guest: !userId, storyId, score: importedScore ?? guest?.score,
    hasFullDraft: full !== null, changed: addition !== null || Boolean(guest?.html) || Boolean(pendingHandoff),
    resolved: false, merging: false, handoff: pendingHandoff, persistedHtml,
  };
  if (pendingHandoff) persistAccountDraft(result, result.html);
  return result;
}

function persistAccountDraft(draft, html) {
  if (isDeletedOwner(Number(draft.key.split(':')[1]))) return false;
  const target = draft.hasFullDraft || draft.resolved ? draft.key : draft.pendingKey;
  let handoff = draft.handoff;
  if (handoff) {
    let prior;
    try { prior = JSON.parse(read(handoff.markerKey)); } catch { /* Create a fresh journal below. */ }
    if (prior?.id === handoff.id && [draft.key, draft.pendingKey].includes(prior.target)
      && (prior.applied || read(prior.target) === prior.merged)) {
      // Never overwrite evidence that the guest text is already durable just
      // because writing the later applied flag or clearing the source failed.
      if (!write(handoff.markerKey, JSON.stringify({ ...prior, applied: true }))) {
        draft.storageError = true;
        return false;
      }
      completeGuestHandoff(handoff.id);
      draft.handoff = null;
      handoff = null;
    }
  }
  const journal = handoff ? { id: handoff.id, target, before: read(target) ?? '', merged: html,
    score: handoff.score, createdAt: Date.now(), applied: false } : null;
  const writeHtml = () => {
    const saved = write(target, html);
    if (saved && target === draft.key) draft.persistedHtml = html;
    return saved;
  };
  if ((handoff && !write(handoff.markerKey, JSON.stringify(journal))) || !writeHtml()
    || (handoff && !write(handoff.markerKey, JSON.stringify({ ...journal, applied: true })))) {
    draft.storageError = true;
    return false;
  }
  draft.storageError = false;
  if (handoff) { completeGuestHandoff(handoff.id); draft.handoff = null; }
  return true;
}

// These are editable paragraphs, not layout padding. Seed only a fresh repost;
// even an intentionally empty recovered draft must keep the author's spacing.
export function initialOpinionHTML(draft, withQuote = false) {
  return withQuote && !draft.hasFullDraft && !draft.changed
    ? '<p></p><p></p>'
    : draft.html;
}

export function updateOpinionDraft(draft, html) {
  if (!draft.guest && isDeletedOwner(Number(draft.key.split(':')[1]))) { draft.html = ''; return; }
  if (draft.merging) return;
  draft.html = html;
  draft.changed = true;
  if (draft.guest) {
    const result = saveGuestDraft(draft.storyId, html, draft.score ?? 4);
    draft.storageError = !result.saved || !result.storageAvailable;
    return;
  }
  persistAccountDraft(draft, html);
}

export function resolveOpinionDraft(draft, editor, savedText) {
  if (!draft.guest && isDeletedOwner(Number(draft.key.split(':')[1]))) { draft.html = ''; return ''; }
  if (draft.guest) { draft.resolved = true; draft.html = editor.getHTML(); return draft.html; }
  // Retries and rerenders must not insert the saved opinion twice or replace
  // any text the user has already typed into a restored full draft.
  if (!draft.hasFullDraft && !draft.resolved && savedText) {
    draft.merging = true;
    try {
      const transaction = editor.chain().setMeta('addToHistory', false);
      if (draft.changed && !editor.isEmpty) {
        // ProseMirror maps the existing selection and undo history through the
        // insertion, keeping the caret in the user's new text.
        transaction.insertContentAt(0, savedText, { updateSelection: false }).run();
      } else {
        transaction.setContent(savedText).command(({ tr }) => {
          tr.setSelection(TextSelection.atEnd(tr.doc));
          return true;
        }).run();
      }
    } finally {
      draft.merging = false;
    }
  }
  draft.resolved = true;
  draft.html = editor.getHTML();
  if ((!draft.changed || persistAccountDraft(draft, draft.html))) remove(draft.pendingKey);
  return draft.html;
}
